import { Box, Button, Grid, MenuItem } from "@mui/material";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import getAxiosError from "../../../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../../../app/features/login/loginSlice";
import {
  RootState,
  useAppDispatch,
} from "../../../../app/features/store/store";
import dayjs from "dayjs";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import Loader from "../../../../components/common/loader/Loader";
import SubmitModalWithFormik from "../../../../components/common/modalPopup/SubmitModalWithFormik";
import { EditRtsRequest } from "../../../../app/models/requestModels/RtsRequestAdminPanel";
import { editRts } from "../../../../app/features/dedicatedDelivery/rtsSlice";
import { FdpAgent } from "../../../../app/api/fdp/FdpAgent";
import { UserDataResponse } from "../../../../app/models/responseModels/userDataUiResponse";
import { StoreResponse } from "../../../../app/models/responseModels/storeResponse";
import { RtsPackageResponse } from "../../../../app/models/responseModels/rtsPackageResponse";
import { MRT_Row } from "material-react-table";
import { FdpResponse } from "../../../../app/models/responseModels/FdpResponse";
import { AddRtsAgent } from "../../../../app/api/rts/AddRtsAgent";
import { RtsEventTypesResponse } from "../../../../app/models/responseModels/rtsEventTypesResponse";
import FormikDatePicker from "../../../../components/common/formikFields/FormikDatePicker";

interface EditRtsModalProps {
  row: MRT_Row<RtsPackageResponse>;
  fdp: FdpResponse | undefined;
  clientIdArg: number;
  clientNameArg: string;
  closeEditRtsModal: CallableFunction;
  onCompleted: CallableFunction;
}
function EditRtsFormModel(
  id: number,
  userId: string,
  storeId: string,
  eventTypeId: number,
  operationDate: string,
  rtsLatitude: string,
  rtsLongitude: string,
  exceptionLatitude: string,
  exceptionLongitude: string,
  rtsScanTimeInGmt: string,
  rtsScanLocalTime: string,
  exceptionScanTimeInGmt: string,
  exceptionScanLocalTime: string,
  scanType: string,
  action: string,
  otherReason?: string,
  barcode?: string,
  imageUrl?: string,
  fdpId?: number
): EditRtsRequest {
  return {
    id: id,
    userId: userId,
    storeId: storeId,
    eventTypeId: eventTypeId,
    operationDate: operationDate,
    rtsLatitude: rtsLatitude,
    rtsLongitude: rtsLongitude,
    exceptionLatitude: exceptionLatitude,
    exceptionLongitude: exceptionLongitude,
    rtsScanTimeInGmt: rtsScanTimeInGmt,
    rtsScanLocalTime: rtsScanLocalTime,
    exceptionScanTimeInGmt: exceptionScanTimeInGmt,
    exceptionScanLocalTime: exceptionScanLocalTime,
    scanType: scanType,
    action: action,
    otherReason: otherReason,
    barcode: barcode,
    imageUrl: imageUrl,
    fdpId: fdpId,
  };
}

const EditRtsModal = ({
  row,
  clientNameArg,
  clientIdArg,
  closeEditRtsModal,
  onCompleted,
}: EditRtsModalProps) => {
  const today = dayjs();
  const dateBeforeThreeMonths = dayjs().subtract(3, "month");
  const dispatch = useAppDispatch();
  const { isFdpLoading, fdpListByClient } = useSelector(
    (state: RootState) => state.fdp
  );

  const { rtsEventTypes } = useSelector((state: RootState) => state.rts);
  const [selectedFdpId, setSelectedFdpId] = useState<number | undefined>(
    row.original.fdpId
  );
  const [dateField, setDateField] = useState<dayjs.Dayjs>(
    dayjs(row.original.operationDate)
  );
  const actionMenu = [
    { label: "Open", value: "open" },
    { label: "In Progress", value: "in_progress" },
    { label: "Closed", value: "closed" },
  ];
  const initialValues: EditRtsRequest = {
    id: row.original.id,
    userId: row.original.userId.toString(),
    storeId: row.original.storeId,
    eventTypeId: row.original.eventTypeId ? row.original.eventTypeId : -1,
    otherReason: row.original.otherReason,
    operationDate: dayjs(row.original.operationDate).toDate(),
    rtsLatitude : row.original.rtsLatitude, 
    rtsLongitude : row.original.rtsLongitude, 
    exceptionLatitude: row.original.exceptionLatitude,
    exceptionLongitude: row.original.exceptionLongitude,
    rtsScanTimeInGmt: row.original.rtsScanTimeInGmt,
    rtsScanLocalTime: row.original.rtsScanLocalTime,
    exceptionScanTimeInGmt: row.original.exceptionScanTimeInGmt,
    exceptionScanLocalTime: row.original.exceptionScanLocalTime,
    scanType: row.original.scanType,
    action: row.original.action,
    barcode: row.original?.barcode,
    imageUrl: row.original?.imageUrl,
    fdpId: selectedFdpId,
  };
  const [serviceLoading, setServiceLoading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [driverData, setDriverData] = useState<UserDataResponse[]>();
  const [storeData, setStoreData] = useState<StoreResponse[]>();
  const [reasonTypes, setReasonTypes] = useState<RtsEventTypesResponse[]>([]);

  useEffect(() => {
    if (rtsEventTypes) {
      setReasonTypes([
        ...rtsEventTypes,
        {
          id: -1,
          eventTypeId: -1,
          eventName: "Other",
          eventDescription: "Other",
        },
      ]);
    }
  }, [rtsEventTypes]);

  // every time, fdp is fetched, corresponding stores and drivers are fetched.
  useEffect(() => {
    if (selectedFdpId) {
      setServiceLoading(true);
      fetchDriversByFdp(selectedFdpId);
      fetchStoresByFdp(selectedFdpId);
      setServiceLoading(false);
    }
  }, [selectedFdpId]);

  const validationSchema = Yup.object().shape({
    userId: Yup.string()
      .required("Driver is required")
      .test(
        "driver-is-not-empty",
        "Driver cannot be blank. ",
        (value) => value !== ""
      ),
    storeId: Yup.number()
      .required("Store is required")
      .test(
        "store-is-not-empty",
        "Store cannot be blank. ",
        (value) => value !== 0
      ),

    eventTypeId: Yup.number()
      .required("Reason is required")
      .test(
        "event-is-not-empty",
        "Reason cannot be blank. ",
        (value) => value !== 0
      ),
    otherReason: Yup.string().test(
      "otherReason-required-if-eventTypeId-is-minus-1",
      "Please specify the reason.",
      function (value: any) {
        // `this.parent` refers to the parent object (the entire form values)
        return this.parent.eventTypeId !== -1 || (value && value.trim() !== "");
      }
    ),
    barcode: Yup.string().test(
      "barcode-max-min-validation",
      "Barcode length can't be less than 3 and greater than 30.",
      (value) => {
        if (value && value !== "") {
          const trimmedValueLength = value.trim().length;
          return trimmedValueLength >= 3 && trimmedValueLength <= 30;
        }
        return true;
      }
    ),
  });

  // ***** Handlers *****
  const handleSubmit = async (values: EditRtsRequest) => {
    var imageUrl: any = undefined;
    if (selectedFile) {
      var files: string[] = [selectedFile];
      await AddRtsAgent.uploadImage({
        files: files,
      }).then(
        (resp) => {
          imageUrl = resp?.imageStatus[0]?.accessUrl;
          submitData(values, imageUrl);
        },
        (err) => {
          toast.error("Error while uploading the image.");
        }
      );
    } else {
      submitData(values);
    }
    return true;
  };

  async function submitData(values: EditRtsRequest, imageUrl?: string) {
    try {
      const EditRtsDetails = EditRtsFormModel(
        values.id,
        values.userId,
        values.storeId,
        values.eventTypeId,
        dayjs(values.operationDate).format("YYYY-MM-DD"),
        row.original.rtsLatitude,
        row.original.rtsLongitude,
        row.original.exceptionLatitude,
        row.original.exceptionLongitude,
        row.original.rtsScanTimeInGmt,
        row.original.rtsScanLocalTime,
        row.original.exceptionScanTimeInGmt,
        row.original.exceptionScanLocalTime,
        row.original.scanType,
        values.action,
        values.eventTypeId === -1 ? values.otherReason?.trim() : undefined,
        values?.barcode,
        imageUrl !== undefined ? imageUrl : row.original.imageUrl
      );
      const response = await dispatch(editRts(EditRtsDetails)).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "rtsData",
        });
      } else {
        toast.success("RTS has been edited successfully.");
        onCompleted();
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "EditRtsException",
      });
    }
  }
  async function fetchDriversByFdp(fdpId: number) {
    try {
      const response = await FdpAgent.getDriversByFdp(fdpId);
      setDriverData(response.data);
    } catch (error) {
      // Handle errors here, e.g., log them or throw a custom error
      console.error("Error fetching drivers by FDP:", error);
      throw error; // Rethrow the error to be handled by the caller if necessary
    }
  }

  async function fetchStoresByFdp(fdpId: number) {
    try {
      const response = await FdpAgent.getStoresByFdpId(fdpId, clientIdArg);
      setStoreData(response.data);
    } catch (error) {
      // Handle errors here, e.g., log them or throw a custom error
      console.error("Error fetching stores by FDP:", error);
      throw error; // Rethrow the error to be handled by the caller if necessary
    }
  }

  // Function to convert a file to base64 format
  function fileToBase64(
    file: File,
    callback: (base64String: string) => void
  ): void {
    const reader = new FileReader();
    reader.onload = function (event) {
      const result = event.target?.result as string;
      callback(result);
    };
    reader.readAsDataURL(file);
  }

  return (
    <>
      <Loader isLoading={isFdpLoading || serviceLoading} />
      <Formik
        initialValues={{ ...initialValues }}
        validationSchema={validationSchema}
        onSubmit={(values: EditRtsRequest, { setSubmitting }) => {
          setSubmitting(true);
          handleSubmit(values);
          setSubmitting(false);
        }}
      >
        {({ values }) => (
          <SubmitModalWithFormik
            title={"Edit " + clientNameArg + " RTS"}
            closeSubmitModal={closeEditRtsModal}
            childErrorMessage={""}
            allowSubmit={false}
            size="lg"
            isLoading={false}
            submitButtonName="Edit"
            closeModalAfterSubmit={true}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Form>
                <Grid container rowSpacing={2} columnSpacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <FormikTextField
                      margin="normal"
                      id="fdpId"
                      name="fdpId"
                      label="FDP"
                      value={selectedFdpId}
                      size="small"
                      fullWidth
                      variant={"outlined"}
                      required
                      select
                      SelectProps={{
                        MenuProps: {
                          PaperProps: { style: { maxHeight: 150 } },
                        },
                      }}
                      onChange={(e: any) => {
                        setSelectedFdpId(e.target.value);
                      }}
                    >
                      {fdpListByClient ? (
                        fdpListByClient.map((fdp, index) => (
                          <MenuItem key={index} value={fdp.id}>
                            {fdp.companyName}
                          </MenuItem>
                        ))
                      ) : (
                        <div></div>
                      )}
                    </FormikTextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      margin="normal"
                      id="userId"
                      name="userId"
                      label="Driver"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                      required
                      select
                      SelectProps={{
                        MenuProps: {
                          PaperProps: { style: { maxHeight: 150 } },
                        },
                      }}
                    >
                      {driverData ? (
                        driverData.map((driver, index) => (
                          <MenuItem
                            key={index}
                            value={driver.id}
                            disabled={driver.deleted}
                          >
                            {driver.firstName} {driver.lastName}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem key={"-1"} value={""}>
                          No Driver Available
                        </MenuItem>
                      )}
                    </FormikTextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      margin="normal"
                      id="storeId"
                      name="storeId"
                      label="Store"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                      required
                      select
                      SelectProps={{
                        MenuProps: {
                          PaperProps: { style: { maxHeight: 150 } },
                        },
                      }}
                    >
                      {storeData !== undefined &&
                        storeData.map((store, index) => (
                          <MenuItem key={index} value={store.id}>
                            {store.storeId}
                          </MenuItem>
                        ))}
                    </FormikTextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      margin="normal"
                      id="eventTypeId"
                      name="eventTypeId"
                      label="Reason"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                      required
                      select
                      SelectProps={{
                        MenuProps: {
                          PaperProps: { style: { maxHeight: 150 } },
                        },
                      }}
                    >
                      {reasonTypes ? (
                        reasonTypes.map((eventType, index) => (
                          <MenuItem key={index} value={eventType.id}>
                            {eventType.eventDescription}
                          </MenuItem>
                        ))
                      ) : (
                        <div></div>
                      )}
                    </FormikTextField>
                  </Grid>
                  {values.eventTypeId === -1 && (
                    <Grid item xs={12} sm={6} md={6} lg={6}>
                      <FormikTextField
                        margin="normal"
                        id="otherReason"
                        name="otherReason"
                        label="Other Reason"
                        size="small"
                        fullWidth
                        variant={"outlined"}
                        required={values.eventTypeId === -1}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: { style: { maxHeight: 150 } },
                          },
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikDatePicker
                      name="operationDate"
                      label="Operation Date"
                      minDate={dateBeforeThreeMonths}
                      maxDate={today}
                      size="small"
                      margin="normal"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      margin="normal"
                      id="action"
                      name="action"
                      label="Action"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                      required
                      select
                      SelectProps={{
                        MenuProps: {
                          PaperProps: { style: { maxHeight: 150 } },
                        },
                      }}
                    >
                      {actionMenu.map((action, index) => (
                        <MenuItem key={index} value={action.value}>
                          {action.label}
                        </MenuItem>
                      ))}
                    </FormikTextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      margin="normal"
                      id="barcode"
                      name="barcode"
                      label="Barcode"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                      autoFocus
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      type="file"
                      style={{ display: "none" }}
                      id="fileInput"
                      onChange={(event: { target: HTMLInputElement }) => {
                        if (event.target.files) {
                          const selectedFile = (
                            event.target as HTMLInputElement
                          ).files?.[0];
                          if (selectedFile) {
                            if (!selectedFile.type.startsWith("image/")) {
                              event.target.files = null;
                              return;
                            }
                            setSelectedFileName(selectedFile.name);
                            fileToBase64(selectedFile, function (base64String) {
                              setSelectedFile(base64String);
                            });
                          }
                        }
                      }}
                      name="fileInput"
                      variant="outlined"
                      label={"Select Image File"}
                    />
                    <label htmlFor="fileInput">
                      <Button variant="outlined" component="span">
                        Select Image File
                      </Button>
                    </label>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    {selectedFileName !== "" ? (
                      <span>{selectedFileName}</span>
                    ) : row.original.imageUrl ? (
                      <img
                        width={200}
                        src={row.original.imageUrl}
                        alt="Selected Image"
                      />
                    ) : (
                      <></>
                    )}
                  </Grid>
                </Grid>
              </Form>
            </Box>
          </SubmitModalWithFormik>
        )}
      </Formik>
    </>
  );
};

export default EditRtsModal;
